import React, { Component, Context } from "react";
import { Route } from "react-router";
import { NavLink, BrowserRouter, Switch } from "react-router-dom";
import { connect } from "react-redux";
import clsx from "clsx";
import debounce from "lodash.debounce";
import { IMarketActionDispatches, mapMarketDispatchToProps } from "../../redux/actions/market";
import { IMarketState, mapMarketStateToProps } from "../../redux/reducers/market";
import UILoader from "../ui/UILoader";
import UISearchInput from "../ui/UISearchInput";
import { WidgetProps } from "../widgets";
import { withAuth, IAuthProps } from "../../laco-common/auth/AuthContext";
import { IAdvertisementFormatted } from "../../laco-common/base/models/advertisement";
import theme from "../../laco-common/theme";
import ClientFirebase from "../../laco-common/firebase/web";
import { IImage } from "../../models/Image";
import UserRoleContent from "../auth/permissions/UserRoleContent";
import LayoutContext, { ILayoutContext } from "../../contexts/layout/LayoutContext";
import ImageView from "../ui/Media/ImageView";
import { IAd } from "../../models/ad";
import AdDetail from "./AdDetail";
import { FileType } from "../../laco-common/base/models/IFile";
import "./Ads.scss";

type AdsProps = {} & WidgetProps;

type AdsState = {
	searchQuery: string;
	loading: boolean;
	ads: IAd[];
	viewAd: IAd;
};

class Ads extends Component<IMarketActionDispatches & IMarketState & AdsProps & IAuthProps, AdsState> {
	static contextType: Context<ILayoutContext> = LayoutContext;
	private readonly handleSearch = debounce(this.onSearch.bind(this), 400, { trailing: true });

	state = {
		searchQuery: "",
		loading: true,
		ads: [],
		viewAd: null,
	};

	get ads(): IAdvertisementFormatted[] {
		if (this.state.searchQuery) {
			const q: string = this.state.searchQuery.toLowerCase();
			return this.state.ads.filter(
				(ad: IAdvertisementFormatted) =>
					ad.name.toLowerCase().includes(q) ||
					ad.description.toLowerCase().includes(q) ||
					ad.price.value.toString().includes(q) ||
					ad.priceFormatted.toLowerCase().includes(q) ||
					ad.dateFormatted.toLowerCase().includes(q)
			);
		}
		return this.state.ads || [];
	}

	componentDidMount() {
		console.log("Ads.componentDidMount()");
		this.init();
	}

	componentDidUpdate(
		prevProps: IMarketActionDispatches & IMarketState & AdsProps & IAuthProps,
		prevState: AdsState,
		snapshot
	) {
		if (prevProps.ads !== this.props.ads || prevProps.ads.length !== this.props.ads.length) {
			this.setAds();
		}
	}

	init(fromRouter?: boolean) {
		Promise.all([this.props.loadAds(this.props.widget.widgetType === "marketplace-preview")]).finally(() => {
			this.setState({ loading: false });
		});
	}

	setAds() {
		const ads: IAd[] = [];
		this.props.ads.forEach((ad: IAdvertisementFormatted) => {
			const images: IImage[] = [];
			(ad.images || []).forEach((filename: string, index: number) => {
				const imageUrl: string = ClientFirebase.getStoragePath("products", ad.id, filename);
				images.push({
					title: ad.name,
					desc: ad.description,
					url: imageUrl,
					filename,
					fileType: FileType.IMAGE,
					order: index,
					preview: false,
				});
			});
			ads.push({
				...ad,
				media: images,
			});
		});
		this.setState({ ads });
	}

	onSearch(val: string) {
		console.log("onSearch:", val);
		this.setState({ searchQuery: val });
	}

	renderMarketplace() {
		return (
			<div
				className={clsx(
					this.context.settings.isMobileRes ? "container-fluid" : "container",
					this.props.isNested && "mt-3 mb-3",
					this.props.widget.widgetType === "marketplace" && !this.props.isNested && "section-padding",
					this.props.widget.widgetType === "marketplace" && "pt-5"
				)}
			>
				{this.props.widget.widgetType === "marketplace-preview" && (
					<h1 className="highlighted mb-4_5 text-center">Members' Marketplace</h1>
				)}
				{this.props.widget.widgetType === "marketplace" && (
					<div className="d-flex">
						<UISearchInput
							className="mb-4 w-100 inverted"
							style={{ maxWidth: "100%" }}
							inputStyle={{ width: "100%" }}
							placeholder="Search marketplace"
							onSearch={this.handleSearch}
						/>
						{/* <UINoData msg="Post through Firewire" className="align-self-center" /> */}
					</div>
				)}

				<UserRoleContent roles={["admin", "user"]}>
					<div className="grid-container">
						{this.ads.map((ad: IAd, i: number) => {
							return (
								<div className="ad-container" key={ad.id}>
									<NavLink to={`/marketplace/${ad.id}`} className="scale-in-image-view">
										<ImageView
											className="mss-image-view"
											image={ad.media[0]}
											isPercentage
											viewWidth={100}
											viewHeight={200}
											displayEnlargeHover={false}
											displayThumb={false}
											renderContainer
										/>
										<div className="ad-detail w-100">
											<h5>{ad.name}</h5>
											<div className="ad-price" style={{ color: theme.colors.orange }}>
												{ad.priceFormatted}
											</div>
											<div className="ad-date">{ad.dateFormatted}</div>
										</div>
									</NavLink>
								</div>
							);
						})}
					</div>
				</UserRoleContent>
			</div>
		);
	}

	render() {
		if (this.state.loading) return <UILoader display={true} />;

		return (
			<div
				className={clsx(
					this.props.widget.widgetType === "marketplace-preview"
						? "firewire-app-outer-container p-0 mb-5"
						: "marketplace-fullpage",
					!this.context.settings.isMobileRes && "container"
				)}
			>
				<div
					className={clsx(
						this.props.widget.widgetType === "marketplace-preview" &&
							"firewire-app-inner-container reverse-border-radius section-padding py-5 mt-2",
						this.context.settings.isMobileRes && "px-3"
					)}
				>
					<BrowserRouter>
						<Switch>
							<Route
								path="/marketplace/:id"
								render={(props) => (
									<>
										{this.renderMarketplace()}
										<AdDetail
											ads={this.state.ads}
											onClose={() => {
												// window.history.replaceState(null, this.props.page?.metaTitle, "/marketplace");
												const originUrl: string = this.props.page?.path;
												props.history.push(`/${originUrl}`);
											}}
											{...props}
										/>
									</>
								)}
							/>
							<Route path="/" render={(props) => this.renderMarketplace()} />
						</Switch>
					</BrowserRouter>

					{this.props.widget.widgetType === "marketplace-preview" && (
						<div className="container mt-5 mb-2">
							<NavLink to={`/marketplace`} className="d-inline-block w-100 text-center mt-0">
								<button className="btn btn-light py-2 px-4 w-100-xs">View All Classifieds</button>
							</NavLink>
						</div>
					)}
				</div>
			</div>
		);
	}
}

export default connect<IMarketState, IMarketActionDispatches, AdsProps>(
	mapMarketStateToProps,
	mapMarketDispatchToProps
)(withAuth(Ads));
